/**
 * BI Litigation Timeline Page
 * 
 * Visual timeline showing:
 * - Claim lifecycle phases and milestones
 * - State-specific deadlines
 * - Predictions and risk assessment
 * - Carrier behavior insights
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Calendar, Clock, AlertTriangle, TrendingUp,
  CheckCircle, Circle, XCircle, AlertCircle, Loader2,
  Building, MapPin, DollarSign, Scale, ChevronRight
} from 'lucide-react';
import AttorneyLayout from '../components/AttorneyLayout';
import { getAuthHeaders } from '../lib/authHeaders';
import { FinancialValue } from '../components/shared/FinancialValue';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface TimelinePhase {
  phase: string;
  name: string;
  description: string;
}

const TIMELINE_PHASES: TimelinePhase[] = [
  { phase: 'claim_submitted', name: 'Claim Submitted', description: 'Initial claim filing' },
  { phase: 'investigation', name: 'Investigation', description: 'Carrier investigation period' },
  { phase: 'proof_of_loss', name: 'Proof of Loss', description: 'POL submission deadline' },
  { phase: 'carrier_review', name: 'Carrier Review', description: 'Coverage decision pending' },
  { phase: 'denial', name: 'Denial/Dispute', description: 'Claim denied or underpaid' },
  { phase: 'appraisal', name: 'Appraisal', description: 'Appraisal process initiated' },
  { phase: 'litigation', name: 'Litigation', description: 'Lawsuit filed' },
  { phase: 'discovery', name: 'Discovery', description: 'Discovery phase' },
  { phase: 'trial', name: 'Trial', description: 'Trial proceedings' },
  { phase: 'resolved', name: 'Resolved', description: 'Case concluded' },
];

export default function BITimelinePage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [timeline, setTimeline] = useState<any>(null);
  const [stateRules, setStateRules] = useState<any>(null);
  const [carrierProfile, setCarrierProfile] = useState<any>(null);
  const [error, setError] = useState('');
  
  // Track whether form has been populated from case data
  const [formPopulatedFromCase, setFormPopulatedFromCase] = useState(false);

  // Form for creating new timeline
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    loss_date: '',
    carrier_name: '',
    state: '',
    claimed_amount: 0,
    claim_reported_date: '',
    insured_name: '',
  });

  // Load existing timeline
  useEffect(() => {
    const fetchTimeline = async () => {
      if (!caseId) {
        setLoading(false);
        setShowCreateForm(true);
        return;
      }

      try {
        const response = await fetch(`${BACKEND_URL}/api/v1/bi-timeline/cases/${caseId}/timeline`, {
          headers: await getAuthHeaders(),
        });

        if (response.status === 404) {
          // Timeline doesn't exist, show create form
          setShowCreateForm(true);
          // Try to load case data to pre-fill form
          await loadCaseData();
        } else if (response.ok) {
          const data = await response.json();
          setTimeline(data);
          // Load state rules and carrier profile
          if (data.state) {
            await loadStateRules(data.state);
          }
          if (data.carrier) {
            await loadCarrierProfile(data.carrier);
          }
        } else {
          throw new Error('Failed to load timeline');
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTimeline();
  }, [caseId]);

  const loadCaseData = async () => {
    if (!caseId) return;

    const authHeaders = await getAuthHeaders();
    if (!authHeaders.Authorization) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney/case/${caseId}`, {
        headers: authHeaders
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          carrier_name: data.carrier || prev.carrier_name,
          state: data.state || prev.state,
          claimed_amount: data.total_demand || prev.claimed_amount,
          insured_name: data.client_name || prev.insured_name,
          loss_date: data.loss_date || prev.loss_date,
        }));
        setFormPopulatedFromCase(true);
      }
    } catch (err) {
      console.error('Failed to load case data:', err);
    }
  };

  // Fetch case context for additional auto-population
  useEffect(() => {
    if (!caseId) return;
    getAuthHeaders().then(authHeaders => fetch(`${BACKEND_URL}/api/v1/attorney/case/${caseId}/context`, {
      headers: authHeaders
    }))
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setFormData(prev => ({
            ...prev,
            // Only populate empty fields from context (don't overwrite user edits or case data)
            loss_date: prev.loss_date || data.loss_date || '',
            carrier_name: prev.carrier_name || data.carrier || '',
            state: prev.state || data.state || '',
            claimed_amount: prev.claimed_amount || data.claim_amount || 0,
            insured_name: prev.insured_name || data.insured_name || '',
          }));
        }
      })
      .catch(err => console.error('Failed to load case context:', err));
  }, [caseId]);

  const loadStateRules = async (state: string) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/bi-timeline/state-rules/${state}`, {
        headers: await getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setStateRules(data);
      }
    } catch (err) {
      console.error('Failed to load state rules:', err);
    }
  };

  const loadCarrierProfile = async (carrier: string) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/bi-timeline/carriers/${encodeURIComponent(carrier)}/profile`, {
        headers: await getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setCarrierProfile(data);
      }
    } catch (err) {
      console.error('Failed to load carrier profile:', err);
    }
  };

  const createTimeline = async () => {
    if (!caseId || !formData.loss_date || !formData.carrier_name || !formData.state) {
      setError('Please fill in all required fields (loss date, carrier, and state)');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/bi-timeline/cases/${caseId}/timeline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to create timeline');
      }

      const data = await response.json();
      setTimeline(data);
      setShowCreateForm(false);
      
      // Load additional data
      await loadStateRules(formData.state);
      await loadCarrierProfile(formData.carrier_name);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const getPhaseStatus = (phase: string) => {
    if (!timeline) return 'pending';
    const currentPhase = timeline.current_phase;
    
    const phaseOrder = TIMELINE_PHASES.map(p => p.phase);
    const currentIdx = phaseOrder.indexOf(currentPhase);
    const phaseIdx = phaseOrder.indexOf(phase);
    
    if (phaseIdx < currentIdx) return 'completed';
    if (phaseIdx === currentIdx) return 'active';
    return 'pending';
  };

  const getRiskColor = (risk: string): React.CSSProperties => {
    switch (risk?.toLowerCase()) {
      case 'low': return { color: 'var(--color-verified)', background: 'transparent' };
      case 'medium': return { color: 'var(--color-needs-human-text)', background: 'var(--color-needs-human-tint)' };
      case 'high': return { color: 'var(--color-needs-human-text)', background: 'var(--color-needs-human-tint)' };
      case 'critical': return { color: 'var(--color-needs-human-text)', background: 'var(--color-needs-human-tint)' };
      default: return { color: 'var(--color-text-muted)', background: 'var(--color-bg-elevated)' };
    }
  };

  if (loading) {
    return (
      <AttorneyLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-brand)' }} />
        </div>
      </AttorneyLayout>
    );
  }

  return (
    <AttorneyLayout>
      {/* Back button */}
      {caseId && (
        <button
          onClick={() => navigate(`/attorney/case/${caseId}`)}
          className="flex items-center gap-2 cs-body mb-6 transition-colors"
          style={{ color: 'var(--color-text-muted)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-text-primary)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; }}
          data-testid="back-to-case"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Case
        </button>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="cs-display flex items-center gap-3">
          <Calendar className="w-7 h-7" style={{ color: 'var(--color-brand)' }} />
          BI Litigation Timeline
        </h1>
        <p className="cs-meta mt-1">Track claim lifecycle, milestones, and predictions</p>
      </div>

      {/* Create Timeline Form */}
      {showCreateForm && !timeline && (
        <div className="rounded-xl p-6 mb-8" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <h3 className="cs-h3 mb-4">Create Timeline</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="cs-label block mb-1">Loss Date *</label>
              <input
                type="date"
                value={formData.loss_date}
                onChange={(e) => setFormData(prev => ({ ...prev, loss_date: e.target.value }))}
                className="cs-body w-full px-3 py-2 rounded-lg"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                data-testid="input-loss-date"
              />
            </div>
            <div>
              <label className="cs-label block mb-1">Carrier *</label>
              <select
                value={formData.carrier_name}
                onChange={(e) => setFormData(prev => ({ ...prev, carrier_name: e.target.value }))}
                className="cs-body w-full px-3 py-2 rounded-lg"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                data-testid="select-carrier"
              >
                <option value="">Select carrier...</option>
                <option value="State Farm">State Farm</option>
                <option value="Allstate">Allstate</option>
                <option value="Travelers">Travelers</option>
                <option value="Liberty Mutual">Liberty Mutual</option>
                <option value="Nationwide">Nationwide</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="cs-label block mb-1">State *</label>
              <select
                value={formData.state}
                onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                className="cs-body w-full px-3 py-2 rounded-lg"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                data-testid="select-state"
              >
                <option value="">Select state...</option>
                {[
                  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
                  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
                  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
                  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
                  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
                  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
                  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
                  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
                  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
                  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
                  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
                  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
                  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
                  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
                  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
                  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
                  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'District of Columbia' },
                ].map(s => (
                  <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="cs-label block mb-1">Claimed Amount ($)</label>
              <input
                type="number"
                value={formData.claimed_amount}
                onChange={(e) => setFormData(prev => ({ ...prev, claimed_amount: parseFloat(e.target.value) || 0 }))}
                className="cs-body w-full px-3 py-2 rounded-lg"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                data-testid="input-claimed-amount"
              />
            </div>
            <div>
              <label className="cs-label block mb-1">Claim Reported Date</label>
              <input
                type="date"
                value={formData.claim_reported_date}
                onChange={(e) => setFormData(prev => ({ ...prev, claim_reported_date: e.target.value }))}
                className="cs-body w-full px-3 py-2 rounded-lg"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
              />
            </div>
            <div>
              <label className="cs-label block mb-1">Insured Name</label>
              <input
                type="text"
                value={formData.insured_name}
                onChange={(e) => setFormData(prev => ({ ...prev, insured_name: e.target.value }))}
                className="cs-body w-full px-3 py-2 rounded-lg"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                placeholder="Business Name"
              />
            </div>
          </div>

          {error && (
            <div className="cs-body mt-4 p-3 rounded-lg" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
              {error}
            </div>
          )}

          <button
            onClick={createTimeline}
            disabled={creating || !formData.loss_date || !formData.carrier_name || !formData.state}
            className="cs-body mt-4 px-6 py-2 rounded-lg disabled:opacity-50 flex items-center gap-2 transition-colors"
            style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
            data-testid="create-timeline-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
            Create Timeline
          </button>
        </div>
      )}

      {/* Timeline View */}
      {timeline && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="rounded-xl p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ background: 'var(--color-needs-human-tint)' }}>
                  <Clock className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                </div>
                <div>
                  <p className="cs-meta">Days Since Loss</p>
                  <p className="cs-figure-lg">{timeline.days_since_loss || 0}</p>
                </div>
              </div>
            </div>

            <div className="rounded-xl p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ background: 'transparent' }}>
                  <DollarSign className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                </div>
                <div>
                  <p className="cs-meta">Claimed Amount</p>
                  <p className="cs-figure-lg"><FinancialValue value={timeline.claimed_amount} /></p>
                </div>
              </div>
            </div>

            <div className="rounded-xl p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={getRiskColor(timeline.prediction?.risk_level)}>
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <p className="cs-meta">Risk Level</p>
                  <p className="cs-figure-lg capitalize">{timeline.prediction?.risk_level || 'Unknown'}</p>
                </div>
              </div>
            </div>

            <div className="rounded-xl p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ background: 'var(--color-needs-human-tint)' }}>
                  <TrendingUp className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                </div>
                <div>
                  <p className="cs-meta">Predicted Outcome</p>
                  <p className="cs-figure capitalize" style={{ color: 'var(--color-text-primary)', fontSize: '1.0625rem', fontWeight: 500 }}>
                    {(timeline.prediction?.outcome || 'pending').replace(/_/g, ' ')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline Visual */}
          <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <h3 className="cs-h3 mb-6">Claim Lifecycle</h3>
            <div className="relative">
              {/* Progress Line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5" style={{ background: 'var(--color-border)' }} />

              {/* Phases */}
              <div className="space-y-6">
                {TIMELINE_PHASES.map((phase, idx) => {
                  const status = getPhaseStatus(phase.phase);
                  return (
                    <div key={phase.phase} className="relative flex items-start gap-4 ml-0">
                      {/* Status Icon */}
                      <div
                        className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                        style={
                          status === 'completed' ? { background: 'var(--color-verified)' } :
                          status === 'active' ? { background: 'var(--color-brand)', boxShadow: '0 0 0 4px var(--color-needs-human-tint)' } :
                          { background: 'var(--color-bg-elevated)' }
                        }
                      >
                        {status === 'completed' && <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-on-brand)' }} />}
                        {status === 'active' && <Circle className="w-5 h-5" style={{ color: 'var(--color-on-brand)', fill: 'var(--color-on-brand)' }} />}
                        {status === 'pending' && <Circle className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />}
                      </div>

                      {/* Phase Content */}
                      <div className={`flex-1 pb-4 ${status === 'pending' ? 'opacity-50' : ''}`}>
                        <div className="flex items-center gap-2">
                          <h4 className="cs-body" style={{ fontWeight: 500, color: status === 'active' ? 'var(--color-needs-human-text)' : 'var(--color-text-primary)' }}>
                            {phase.name}
                          </h4>
                          {status === 'active' && (
                            <span className="cs-chip px-2 py-0.5 rounded-full" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                              Current
                            </span>
                          )}
                        </div>
                        <p className="cs-meta">{phase.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Upcoming Deadlines */}
          {timeline.upcoming_deadlines && timeline.upcoming_deadlines.length > 0 && (
            <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <h3 className="cs-h3 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                Upcoming Deadlines
              </h3>
              <div className="space-y-3">
                {timeline.upcoming_deadlines.map((deadline: any, idx: number) => (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
                    <div>
                      <p className="cs-body capitalize" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{deadline.type?.replace(/_/g, ' ')}</p>
                      <p className="cs-meta">{deadline.deadline}</p>
                    </div>
                    <div
                      className="cs-chip px-3 py-1 rounded-full"
                      style={
                        deadline.days_remaining < 7 ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' } :
                        deadline.days_remaining < 30 ? { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' } :
                        { background: 'transparent', color: 'var(--color-verified)' }
                      }
                    >
                      {deadline.days_remaining} days
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Overdue Items */}
          {timeline.overdue_items && timeline.overdue_items.length > 0 && (
            <div className="rounded-xl p-6" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
              <h3 className="cs-h3 mb-4 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
                <XCircle className="w-5 h-5" />
                Overdue Items ({timeline.overdue_items.length})
              </h3>
              <div className="space-y-2">
                {timeline.overdue_items.map((item: any, idx: number) => (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'var(--color-bg-surface)' }}>
                    <span className="cs-body capitalize" style={{ color: 'var(--color-text-primary)' }}>{item.type?.replace(/_/g, ' ')}</span>
                    <span className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>{item.days_overdue} days overdue</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Risk Factors */}
          {timeline.risk_factors && timeline.risk_factors.length > 0 && (
            <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <h3 className="cs-h3 mb-4">Risk Factors</h3>
              <div className="space-y-2">
                {timeline.risk_factors.map((factor: string, idx: number) => (
                  <div key={idx} className="flex items-center gap-2 cs-body">
                    <AlertTriangle className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
                    <span>{factor}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* State Rules & Carrier Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* State Rules */}
            {stateRules && (
              <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                <h3 className="cs-h3 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  {stateRules.state_name} Rules
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="cs-meta">POL Deadline</span>
                    <span className="cs-body" style={{ fontWeight: 500 }}>{stateRules.proof_of_loss?.deadline_days} days</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="cs-meta">Carrier Decision</span>
                    <span className="cs-body" style={{ fontWeight: 500 }}>{stateRules.carrier_response?.decision_days} days</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="cs-meta">Attorney Fee Shifting</span>
                    <span className="cs-body" style={{ fontWeight: 500 }}>{stateRules.remedies?.attorney_fee_shifting ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="cs-meta">Bad Faith Notice Required</span>
                    <span className="cs-body" style={{ fontWeight: 500 }}>{stateRules.bad_faith?.notice_required ? 'Yes' : 'No'}</span>
                  </div>
                  {stateRules.special_rules && stateRules.special_rules.length > 0 && (
                    <div className="pt-2" style={{ borderTop: '1px solid var(--color-border)' }}>
                      <p className="cs-meta mb-1">Special Rules:</p>
                      <ul className="list-disc list-inside cs-body space-y-1">
                        {stateRules.special_rules.slice(0, 3).map((rule: string, idx: number) => (
                          <li key={idx} className="cs-meta">{rule}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Carrier Profile */}
            {carrierProfile && (
              <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                <h3 className="cs-h3 mb-4 flex items-center gap-2">
                  <Building className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  {carrierProfile.carrier_name} Profile
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="cs-meta">Denial Rate</span>
                    <span className="cs-figure" style={{ fontWeight: 500 }}>{(carrierProfile.outcome_patterns?.denial_rate * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="cs-meta">Underpayment Rate</span>
                    <span className="cs-figure" style={{ fontWeight: 500 }}>{(carrierProfile.outcome_patterns?.underpayment_rate * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="cs-meta">Avg Days to Payment</span>
                    <span className="cs-figure" style={{ fontWeight: 500 }}>{carrierProfile.response_patterns?.avg_days_to_first_payment} days</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="cs-meta">Litigation Rate</span>
                    <span className="cs-figure" style={{ fontWeight: 500 }}>{(carrierProfile.outcome_patterns?.litigation_rate * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="cs-meta">Avg Settlement %</span>
                    <span className="cs-figure" style={{ fontWeight: 500 }}>{(carrierProfile.settlement_patterns?.avg_settlement_percentage * 100).toFixed(0)}%</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Prediction Details */}
          {timeline.prediction && (
            <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <h3 className="cs-h3 mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                Outcome Prediction
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="cs-meta">Predicted Outcome</p>
                  <p className="cs-body capitalize" style={{ color: 'var(--color-text-primary)', fontSize: '1.0625rem', fontWeight: 500 }}>{timeline.prediction.outcome?.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <p className="cs-meta">Confidence</p>
                  <p className="cs-figure" style={{ color: 'var(--color-text-primary)', fontSize: '1.0625rem', fontWeight: 500 }}>{timeline.prediction.confidence}</p>
                </div>
                <div>
                  <p className="cs-meta">Resolution Date</p>
                  <p className="cs-figure" style={{ color: 'var(--color-text-primary)', fontSize: '1.0625rem', fontWeight: 500 }}>{timeline.prediction.resolution_date || 'TBD'}</p>
                </div>
                <div>
                  <p className="cs-meta">Bad Faith Probability</p>
                  <p className="cs-figure" style={{ color: 'var(--color-text-primary)', fontSize: '1.0625rem', fontWeight: 500 }}>{timeline.prediction.bad_faith_probability}</p>
                </div>
              </div>

              {timeline.prediction.recovery_range && (
                <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <p className="cs-meta mb-2">Recovery Range</p>
                  <div className="flex items-center gap-4">
                    <span className="cs-figure">Low: <FinancialValue value={timeline.prediction.recovery_range.low} /></span>
                    <ChevronRight className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                    <span className="cs-figure" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>Likely: <FinancialValue value={timeline.prediction.recovery_range.likely} /></span>
                    <ChevronRight className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                    <span className="cs-figure">High: <FinancialValue value={timeline.prediction.recovery_range.high} /></span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </AttorneyLayout>
  );
}
